import type { Module } from 'vuex';
import type { GlobalPairState } from './globalPair';
import type { GlobalPairsState } from './globalPairs';
import type { CurrenciesState } from './currencies';
import type { GlobalCurrenciesState } from './globalCurrencies';
import type { GlobalPairIndicatorSetsState } from './globalPairIndicatorSets';
import {RootState} from "@/store";

export interface ComputedDataState {
  // Пустое состояние, так как это модуль только для вычислений
}

// Типизированный интерфейс для rootState в геттерах
interface TypedRootState {
  globalPair: GlobalPairState;
  globalPairs: GlobalPairsState;
  currencies: CurrenciesState;
  globalCurrencies: GlobalCurrenciesState;
  globalPairIndicatorSets: GlobalPairIndicatorSetsState;
}

const computedDataModule: Module<ComputedDataState, RootState> = {
  namespaced: true,

  state: (): ComputedDataState => ({}),

  getters: {
    // Геттер для получения кода глобальной пары (например, "BTC-USD")
    getGlobalPairCode: (state, getters, rootState, rootGetters) => (idGlobalPair: number): string | null => {
      const typedRootState = rootState as TypedRootState;
      
      // Сначала проверяем в модуле конкретной пары
      let globalPair = typedRootState.globalPair.globalPair;
      
      // Если в конкретном модуле нет данных или ID не совпадает, ищем в списке
      if (!globalPair || globalPair.idGlobalPair !== idGlobalPair) {
        globalPair = typedRootState.globalPairs.globalPairs.find(gp => gp.idGlobalPair === idGlobalPair) || null;
      }
      
      if (!globalPair) {
        return null;
      }
      
      // Получаем базовую валюту
      const baseCurrency = typedRootState.currencies.currencies.find(c => c.idCurrency === globalPair.idCurrency);
      
      // Получаем глобальную валюту (котируемую) и связанную с ней валюту
      const globalCurrency = typedRootState.globalCurrencies.globalCurrencies.find(gc => gc.idGlobalCurrency === globalPair.idGlobalCurrency);
      const quotedCurrency = typedRootState.currencies.currencies.find(c => c.idCurrency === globalCurrency?.idCurrency);
      
      if (!baseCurrency || !quotedCurrency) {
        return null;
      }
      
      return `${baseCurrency.code}-${quotedCurrency.code}`;
    },

    // Геттер для получения доступных индикаторных наборов для глобальной пары
    getAvailableIndicatorSets: (state, getters, rootState, rootGetters) => (idGlobalPair: number) => {
      const typedRootState = rootState as TypedRootState;
      
      // Получаем все idIndicatorSet, связанные с данной парой
      const usedIds = typedRootState.globalPairIndicatorSets.globalPairIndicatorSets
        .filter(item => item.idGlobalPair === idGlobalPair)
        .map(item => item.idIndicatorSet);
      
      // Получаем все доступные indicatorSets
      const allIndicatorSets = rootGetters['indicatorSets/getAll'] || [];
      
      // Фильтруем те, которые еще не связаны
      return allIndicatorSets.filter((set: any) => !usedIds.includes(set.idIndicatorSet));
    },

    // Геттер для получения имени индикаторного набора
    getIndicatorSetName: (state, getters, rootState, rootGetters) => (idIndicatorSet: number): string => {
      const indicatorSet = rootGetters['indicatorSets/getById']?.(idIndicatorSet);
      if (!indicatorSet) return 'null';
      
      const indicator = rootGetters['indicators/getById']?.(indicatorSet.idIndicator);
      if (!indicator) return 'null';
      
      return `${indicator.name} -- ${indicatorSet.idIndicatorSet}`;
    },
  },

  mutations: {
    // Пустые мутации, так как это модуль только для вычислений
  },

  actions: {
    // Пустые действия, так как это модуль только для вычислений
  },
};

export default computedDataModule; 